import { useState, useRef, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { abaDoCondominio, useMenu } from '@/lib/menu';
import Logo from '@/components/base/Logo';

interface CondoItem {
  id: string;
  nome: string;
  endereco?: {
    cidade?: string;
    estado?: string;
  };
}

interface ContaCliente {
  accountId: string;
  nome: string;
  email: string;
  tipo: string;
  documento: string;
  cidade: string;
  estado: string;
  planoId: string;
  subscriptionStatus: string;
}

const planoLabels: Record<string, string> = {
  'plano-essencial': 'Essencial',
  'plano-profissional': 'Profissional',
  'plano-enterprise': 'Enterprise',
};

const statusLabels: Record<string, string> = {
  active: 'Ativo',
  canceled: 'Cancelado',
  past_due: 'Atrasado',
  incomplete: 'Incompleto',
};

const statusColors: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-700',
  canceled: 'bg-red-100 text-red-700',
  past_due: 'bg-red-100 text-red-700',
  incomplete: 'bg-gray-100 text-gray-600',
};

interface SidebarItem {
  label: string;
  icon: string;
  href: string;
  active?: boolean;
  /** Falso desenha o item cinza, sem rota (migration 0041). */
  permitido?: boolean;
}

/** Texto único para o item que existe e o usuário não alcança. */
const SEM_PERMISSAO = 'Você não tem permissão para acessar esta área.';

// Os cinco arrays de menu que viviam aqui sairam: a estrutura vem de
// `menu_do_usuario()` (migration 0036), ja filtrada pelas permissoes de quem
// pediu. O que o papel nao alcanca nao chega ao navegador — antes chegava, e
// o DevTools mostrava a area administrativa inteira para qualquer um.

export default function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    user,
    isLoggedIn,
    isSuperuser,
    logout,
    effectiveAccountId,
    impersonatedAccount,
    setImpersonatedAccount,
  } = useAuth();
  // Recarrega quando o superusuario impersona outra conta: a funcao e
  // `security invoker` e responde pelo que a sessao permite.
  const { menu, carregando: menuCarregando, erro: menuErro } = useMenu(effectiveAccountId);
  const [collapsed, setCollapsed] = useState(false);
  const [condoSelectorOpen, setCondoSelectorOpen] = useState(false);
  const [condominios, setCondominios] = useState<CondoItem[]>([]);
  const selectorRef = useRef<HTMLDivElement>(null);
  const clientSelectorRef = useRef<HTMLDivElement>(null);

  // Client accounts for superuser impersonation
  const [clientAccounts, setClientAccounts] = useState<ContaCliente[]>([]);
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [clientLoading, setClientLoading] = useState(false);

  // Fetch client accounts for superuser with enriched data
  useEffect(() => {
    if (!isSuperuser) {
      setClientAccounts([]);
      return;
    }
    setClientLoading(true);
    supabase
      .from('profiles')
      .select(
        'account_id, nome, email, tipo, cnpj, cpf, cidade, estado, plano_id, subscription_status',
      )
      .not('account_id', 'is', null)
      .neq('email', 'admin@centralcheck.com.br')
      .order('nome', { ascending: true })
      .then(({ data, error }) => {
        if (!error && data) {
          const contas: ContaCliente[] = [];
          const seen = new Set<string>();
          for (const row of data) {
            const accountId = row.account_id as string;
            if (!seen.has(accountId)) {
              seen.add(accountId);
              const tipo = (row.tipo as string) || 'pj';
              const documento =
                tipo === 'pj' ? (row.cnpj as string) || '' : (row.cpf as string) || '';
              contas.push({
                accountId,
                nome: row.nome || 'Sem nome',
                email: row.email || '',
                tipo,
                documento,
                cidade: row.cidade || '',
                estado: row.estado || '',
                planoId: row.plano_id as string,
                subscriptionStatus: row.subscription_status as string,
              });
            }
          }
          setClientAccounts(contas);
        }
        setClientLoading(false);
      });
  }, [isSuperuser, impersonatedAccount]);

  const filteredClients = useMemo(() => {
    if (!clientSearch.trim()) return clientAccounts;
    const q = clientSearch.toLowerCase();
    return clientAccounts.filter(
      (c) =>
        c.nome.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q) ||
        c.documento.toLowerCase().includes(q) ||
        c.cidade.toLowerCase().includes(q) ||
        c.estado.toLowerCase().includes(q),
    );
  }, [clientAccounts, clientSearch]);

  // Fetch condominios from Supabase with proper account filtering
  useEffect(() => {
    let query = supabase
      .from('condominios')
      .select('id, nome, endereco')
      .order('nome', { ascending: true });

    if (isSuperuser && impersonatedAccount) {
      query = query.eq('account_id', impersonatedAccount.accountId);
    } else if (!isSuperuser && effectiveAccountId) {
      query = query.eq('account_id', effectiveAccountId);
    }

    query.then(({ data, error }) => {
      if (!error && data) setCondominios(data as CondoItem[]);
    });
  }, [isSuperuser, effectiveAccountId, impersonatedAccount]);

  const condoMatch = location.pathname.match(/^\/condominios\/([^/]+)(?:\/([^/]+))?/);
  const isCondoDetail = !!condoMatch;
  const currentCondoId = condoMatch ? condoMatch[1] : null;
  const currentTab = condoMatch && condoMatch[2] ? condoMatch[2] : 'resumo';
  const currentCondo = condominios.find((c) => c.id === currentCondoId);

  const dashboardMatch = location.pathname.match(/^\/dashboard(?:\/([^/]+))?/);
  const isDashboardDetail = !!dashboardMatch;
  const currentDashboardTab =
    dashboardMatch && dashboardMatch[1] ? dashboardMatch[1] : 'alertas-kpis';

  const adminMatch = location.pathname.match(/^\/admin(?:\/([^/]+))?/);
  const isAdminPage = !!adminMatch;
  const currentAdminTab = adminMatch && adminMatch[1] ? adminMatch[1] : '';

  const chevronIcon = condoSelectorOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line';

  // O menu vem do servidor ja filtrado. Aqui so se traduz para a forma que o
  // JSX abaixo sempre usou (`label`/`icon`), em vez de reescrever a arvore de
  // renderizacao inteira.
  const filhosDe = (
    raizes: {
      id: string;
      filhos: {
        id: string;
        rotulo: string;
        icone: string;
        href: string | null;
        permitido?: boolean;
      }[];
    }[],
    id: string,
    prefixo: string,
  ) =>
    (raizes.find((i) => i.id === id)?.filhos ?? []).map((f) => ({
      id: f.id.startsWith(prefixo) ? f.id.slice(prefixo.length) : f.id,
      label: f.rotulo,
      icon: f.icone,
      href: f.href ?? '#',
      // Ausente significa banco na 0036, que so mandava o que era permitido.
      permitido: f.permitido ?? true,
    }));

  const mainMenu: SidebarItem[] = menu.principal.map((i) => ({
    label: i.rotulo,
    icon: i.icone,
    href: i.href ?? '#',
    permitido: i.permitido ?? true,
  }));

  const dashboardSubItems = filhosDe(menu.principal, 'dashboard', 'dashboard.');
  const adminSubItems = filhosDe(menu.principal, 'admin', 'admin.');
  const subMenuItems = menu.condominio.map((i) => ({
    id: abaDoCondominio(i.id),
    label: i.rotulo,
    icon: i.icone,
    permitido: i.permitido ?? true,
  }));

  // O rodape deixa de ser tres links fixos: Gestao do Plano some para quem nao
  // tem `plano.ler` — o sindico, por exemplo.
  const rodapeMenu = menu.rodape.map((i) => ({
    id: i.id,
    label: i.rotulo,
    icon: i.icone,
    href: i.href ?? '#',
    permitido: i.permitido ?? true,
  }));

  // Click outside handlers
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (selectorRef.current && !selectorRef.current.contains(e.target as Node)) {
        setCondoSelectorOpen(false);
      }
      if (clientSelectorRef.current && !clientSelectorRef.current.contains(e.target as Node)) {
        setClientDropdownOpen(false);
        setClientSearch('');
      }
    }
    if (condoSelectorOpen || clientDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [condoSelectorOpen, clientDropdownOpen]);

  const isActive = (href: string) => {
    if (href === '/dashboard' && location.pathname.startsWith('/dashboard')) return true;
    if (href === '/saude-pendencias' && location.pathname === '/saude-pendencias') return true;
    if (href === '/prestadores' && location.pathname === '/prestadores') return true;
    if (href === '/ordens-servico' && location.pathname === '/ordens-servico') return true;
    if (href === '/condominios' && location.pathname.startsWith('/condominios')) return true;
    if (href === '/admin' && location.pathname.startsWith('/admin')) return true;
    if (href === '/gestao-plano' && location.pathname.startsWith('/gestao-plano')) return true;
    return false;
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleClientSelect = (conta: ContaCliente) => {
    setImpersonatedAccount({
      accountId: conta.accountId,
      nome: conta.nome,
      email: conta.email,
    });
    setClientDropdownOpen(false);
    setClientSearch('');
    // Navigate to dashboard to refresh the view
    navigate('/dashboard');
  };

  const handleExitImpersonation = () => {
    setImpersonatedAccount(null);
    setClientDropdownOpen(false);
    setClientSearch('');
    navigate('/admin');
  };

  const userIniciais = user?.nome
    ? user.nome
        .split(' ')
        .map((n) => n[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : '??';

  const userName = impersonatedAccount ? impersonatedAccount.nome : user?.nome || 'Usuário';
  const userRole = impersonatedAccount
    ? 'Visualizando como cliente'
    : isSuperuser
      ? 'Super Admin'
      : 'Síndico / Gestor';
  const userRoleIcon = impersonatedAccount
    ? 'ri-eye-line'
    : isSuperuser
      ? 'ri-shield-star-fill'
      : 'ri-user-3-line';

  return (
    <aside
      className={`h-screen sticky top-0 flex flex-col bg-background-50 border-r border-background-200/70 transition-all duration-300 ${
        collapsed ? 'w-16' : 'w-60'
      }`}
    >
      <div
        className={`flex items-center h-16 border-b border-background-200/70 ${collapsed ? 'justify-center px-2' : 'px-5'}`}
      >
        <Link to={isSuperuser ? '/admin' : '/dashboard'} className="flex items-center gap-2.5">
          <Logo className="w-8 h-8 object-contain shrink-0" />
          {!collapsed && (
            <span className="font-semibold text-sm tracking-tight text-foreground-900 whitespace-nowrap">
              Central Check
            </span>
          )}
        </Link>
      </div>

      {/* Client selector for superuser */}
      {isSuperuser && !collapsed && (
        <div className="px-3 pt-3 pb-1" ref={clientSelectorRef}>
          <div className="relative">
            <button
              onClick={() => setClientDropdownOpen(!clientDropdownOpen)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-colors border ${
                impersonatedAccount
                  ? 'bg-accent-50 border-accent-300 text-accent-800'
                  : 'bg-background-100 border-background-200/70 text-foreground-600 hover:bg-background-200/50'
              }`}
            >
              <div
                className={`w-5 h-5 flex items-center justify-center shrink-0 ${impersonatedAccount ? 'text-accent-600' : 'text-foreground-400'}`}
              >
                <i
                  className={`${impersonatedAccount ? 'ri-eye-line' : 'ri-user-search-line'} text-sm`}
                ></i>
              </div>
              <span className="truncate flex-1 text-left text-xs">
                {impersonatedAccount
                  ? `Cliente: ${impersonatedAccount.nome}`
                  : 'Selecionar cliente'}
              </span>
              <div className="w-4 h-4 flex items-center justify-center">
                <i
                  className={`${clientDropdownOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-xs`}
                ></i>
              </div>
            </button>

            {clientDropdownOpen && (
              <div className="absolute left-0 right-0 mt-1 bg-background-50 border border-background-200/70 rounded-lg shadow-lg z-50 max-h-[28rem] overflow-hidden flex flex-col">
                {/* Search bar */}
                <div className="px-2.5 pt-2.5 pb-1.5 border-b border-background-200/50">
                  <div className="relative">
                    <div className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-400">
                      <i className="ri-search-line text-xs"></i>
                    </div>
                    <input
                      type="text"
                      placeholder="Buscar cliente..."
                      value={clientSearch}
                      onChange={(e) => setClientSearch(e.target.value)}
                      className="w-full pl-8 pr-3 py-1.5 text-xs bg-background-100 border border-background-200/60 rounded-md text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                      autoFocus
                    />
                  </div>
                </div>

                {/* Scrollable list */}
                <div className="overflow-y-auto flex-1">
                  {/* Option to exit impersonation */}
                  {impersonatedAccount && (
                    <button
                      onClick={handleExitImpersonation}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-accent-700 hover:bg-accent-50 transition-colors border-b border-background-200/50"
                    >
                      <div className="w-5 h-5 flex items-center justify-center text-accent-600">
                        <i className="ri-arrow-go-back-line text-sm"></i>
                      </div>
                      <span>Voltar à visão de admin</span>
                    </button>
                  )}

                  {clientLoading && (
                    <div className="px-3 py-4 text-center">
                      <div className="w-4 h-4 flex items-center justify-center mx-auto">
                        <i className="ri-loader-4-line animate-spin text-foreground-400 text-sm"></i>
                      </div>
                      <p className="text-xs text-foreground-500 mt-1">Carregando clientes...</p>
                    </div>
                  )}

                  {!clientLoading && filteredClients.length === 0 && (
                    <div className="px-3 py-4 text-center">
                      <p className="text-xs text-foreground-500">
                        {clientSearch.trim()
                          ? 'Nenhum cliente encontrado'
                          : 'Nenhum cliente cadastrado'}
                      </p>
                    </div>
                  )}

                  {filteredClients.map((conta) => {
                    const isActive = impersonatedAccount?.accountId === conta.accountId;
                    const planoLabel = planoLabels[conta.planoId] || conta.planoId;
                    const statusLabel =
                      statusLabels[conta.subscriptionStatus] || conta.subscriptionStatus;
                    const statusClass =
                      statusColors[conta.subscriptionStatus] || 'bg-gray-100 text-gray-600';
                    return (
                      <button
                        key={conta.accountId}
                        onClick={() => handleClientSelect(conta)}
                        className={`w-full flex items-start gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-background-100 ${
                          isActive ? 'bg-accent-50' : ''
                        }`}
                      >
                        <div
                          className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-xs font-semibold mt-0.5 ${
                            isActive
                              ? 'bg-accent-200 text-accent-700'
                              : 'bg-background-100 text-foreground-500'
                          }`}
                        >
                          {conta.nome.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <p
                              className={`truncate text-sm ${isActive ? 'text-accent-700 font-medium' : 'text-foreground-700'}`}
                            >
                              {conta.nome}
                            </p>
                            <span
                              className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${
                                conta.tipo === 'pj'
                                  ? 'bg-primary-100 text-primary-700'
                                  : 'bg-secondary-100 text-secondary-700'
                              }`}
                            >
                              {conta.tipo === 'pj' ? 'PJ' : 'PF'}
                            </span>
                          </div>
                          {conta.documento && (
                            <p className="text-[11px] text-foreground-500 truncate mt-0.5">
                              {conta.documento}
                            </p>
                          )}
                          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                            {conta.cidade && conta.estado && (
                              <span className="text-[10px] text-foreground-400 flex items-center gap-0.5">
                                <i className="ri-map-pin-line text-[10px]"></i>
                                {conta.cidade}/{conta.estado}
                              </span>
                            )}
                            {planoLabel && (
                              <span className="text-[10px] px-1 py-0.5 rounded bg-background-100 text-foreground-500 font-medium">
                                {planoLabel}
                              </span>
                            )}
                            <span
                              className={`text-[10px] px-1 py-0.5 rounded-full font-medium ${statusClass}`}
                            >
                              {statusLabel}
                            </span>
                          </div>
                        </div>
                        {isActive && (
                          <div className="w-4 h-4 flex items-center justify-center text-accent-600 ml-auto shrink-0 mt-1">
                            <i className="ri-check-line text-sm"></i>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Footer count */}
                {!clientLoading && filteredClients.length > 0 && (
                  <div className="px-3 py-2 border-t border-background-200/50 bg-background-50">
                    <p className="text-[10px] text-foreground-400 text-center">
                      {filteredClients.length} de {clientAccounts.length} cliente
                      {clientAccounts.length !== 1 ? 's' : ''}
                      {clientSearch.trim() ? ' (filtrado)' : ''}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Impersonation banner when collapsed */}
      {isSuperuser && impersonatedAccount && collapsed && (
        <div className="px-2 pt-3 pb-1 flex justify-center">
          <div
            className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center"
            title={`Visualizando: ${impersonatedAccount.nome}`}
          >
            <i className="ri-eye-line text-accent-600 text-sm"></i>
          </div>
        </div>
      )}

      <nav className="flex-1 py-4 px-3 overflow-y-auto">
        {/* Menu vazio precisa dizer por que esta vazio.
            O menu vem do servidor ja filtrado por permissao, entao "nenhum
            item" e um estado legitimo — e tambem o sintoma de uma conta sem
            papel atribuido. Nos dois casos, area em branco nao informa nada:
            parece tela quebrada e nao sugere o que fazer. */}
        {!menuCarregando && mainMenu.length === 0 && !collapsed && (
          <p className="px-3 py-2 text-xs leading-relaxed text-foreground-400">
            {menuErro ? (
              <>
                Não foi possível carregar o menu. Recarregue a página; se continuar, avise o
                suporte.
              </>
            ) : (
              <>
                Nenhum item disponível para este perfil. Se você administra a conta, as permissões
                ainda não foram atribuídas — fale com o suporte.
              </>
            )}
          </p>
        )}
        <ul className="flex flex-col gap-1">
          {mainMenu.map((item) => {
            const active = isActive(item.href);
            const isCondominios = item.href === '/condominios';
            const isDashboard = item.href === '/dashboard';
            const showCondoSubmenu = isCondominios && isCondoDetail && !collapsed;
            const showDashboardSubmenu = isDashboard && isDashboardDetail && !collapsed;

            // Dashboard sub-items
            if (showDashboardSubmenu) {
              return (
                <li key={item.href}>
                  <div className="mb-1">
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium bg-primary-50 text-primary-700">
                      <div className="w-5 h-5 flex items-center justify-center text-primary-600">
                        <i className={`${item.icon} text-lg`}></i>
                      </div>
                      <span className="whitespace-nowrap flex-1">{item.label}</span>
                    </div>
                  </div>
                  <ul className="flex flex-col gap-0.5 ml-2 pl-3 border-l-2 border-background-200/60">
                    {dashboardSubItems.map((sub) => {
                      const subActive = currentDashboardTab === sub.id;
                      return (
                        <li key={sub.id}>
                          <Link
                            to={`/dashboard/${sub.id}`}
                            className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-all ${
                              subActive
                                ? 'bg-primary-50 text-primary-700'
                                : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-800'
                            }`}
                          >
                            <div
                              className={`w-3.5 h-3.5 flex items-center justify-center ${subActive ? 'text-primary-600' : 'text-foreground-400'}`}
                            >
                              <i className={`${sub.icon} text-xs`}></i>
                            </div>
                            {sub.label}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              );
            }

            // Admin sub-items
            if (item.href === '/admin' && isAdminPage && !collapsed) {
              return (
                <li key={item.href}>
                  <div className="mb-1">
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium bg-primary-50 text-primary-700">
                      <div className="w-5 h-5 flex items-center justify-center text-primary-600">
                        <i className={`${item.icon} text-lg`}></i>
                      </div>
                      <span className="whitespace-nowrap flex-1">{item.label}</span>
                    </div>
                  </div>
                  <ul className="flex flex-col gap-0.5 ml-2 pl-3 border-l-2 border-background-200/60">
                    {adminSubItems.map((sub) => {
                      const subActive = (currentAdminTab || 'clientes') === sub.id;
                      return (
                        <li key={sub.id}>
                          <Link
                            to={sub.href}
                            className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-all ${
                              subActive
                                ? 'bg-primary-50 text-primary-700'
                                : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-800'
                            }`}
                          >
                            <div
                              className={`w-3.5 h-3.5 flex items-center justify-center ${subActive ? 'text-primary-600' : 'text-foreground-400'}`}
                            >
                              <i className={`${sub.icon} text-xs`}></i>
                            </div>
                            {sub.label}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              );
            }

            // O submenu de Gestao do Plano vivia aqui e era inalcancavel: o item
            // esta no rodape, e este laco percorre so o menu principal. O ramo
            // saiu junto com os arrays — codigo que nunca roda descreve uma
            // tela que nao existe.

            if (showCondoSubmenu && currentCondo) {
              return (
                <li key={item.href}>
                  <div className="mb-1">
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium bg-primary-50 text-primary-700">
                      <div className="w-5 h-5 flex items-center justify-center text-primary-600">
                        <i className={`${item.icon} text-lg`}></i>
                      </div>
                      <span className="whitespace-nowrap flex-1">{item.label}</span>
                      <Link
                        to="/condominios"
                        className="w-5 h-5 flex items-center justify-center text-primary-500 hover:text-primary-700 transition-colors"
                        title="Ver todos os condomínios"
                      >
                        <i className="ri-arrow-go-back-line text-sm"></i>
                      </Link>
                    </div>
                  </div>

                  <div className="mb-2" ref={selectorRef}>
                    <button
                      onClick={() => setCondoSelectorOpen(!condoSelectorOpen)}
                      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors"
                    >
                      <div className="w-5 h-5 rounded bg-background-100 flex items-center justify-center shrink-0">
                        <i className="ri-map-pin-line text-foreground-500 text-xs"></i>
                      </div>
                      <span className="truncate flex-1 text-left">{currentCondo.nome}</span>
                      <div className="w-4 h-4 flex items-center justify-center text-foreground-400">
                        <i className={`${chevronIcon} text-xs`}></i>
                      </div>
                    </button>

                    {condoSelectorOpen && (
                      <div className="absolute left-3 right-3 mt-1 bg-background-50 border border-background-200/70 rounded-lg shadow-lg z-50 max-h-56 overflow-y-auto">
                        {condominios.map((condo) => (
                          <Link
                            key={condo.id}
                            to={`/condominios/${condo.id}/${currentTab}`}
                            onClick={() => setCondoSelectorOpen(false)}
                            className={`flex items-center gap-2.5 px-3 py-2.5 text-sm transition-colors hover:bg-background-100 ${
                              condo.id === currentCondoId
                                ? 'bg-primary-50 text-primary-700 font-medium'
                                : 'text-foreground-700'
                            }`}
                          >
                            <div className="w-5 h-5 rounded bg-background-100 flex items-center justify-center shrink-0">
                              <i
                                className={`ri-building-line ${condo.id === currentCondoId ? 'text-primary-600' : 'text-foreground-500'} text-xs`}
                              ></i>
                            </div>
                            <div className="min-w-0">
                              <p className="truncate text-sm">{condo.nome}</p>
                              <p className="text-xs text-foreground-500 truncate">
                                {condo.endereco?.cidade}, {condo.endereco?.estado}
                              </p>
                            </div>
                            {condo.id === currentCondoId && (
                              <div className="w-4 h-4 flex items-center justify-center text-primary-600 ml-auto shrink-0">
                                <i className="ri-check-line text-sm"></i>
                              </div>
                            )}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>

                  <ul className="flex flex-col gap-0.5 ml-2 pl-3 border-l-2 border-background-200/60">
                    {subMenuItems.map((sub) => {
                      const subActive = currentTab === sub.id;

                      // Aba fora do alcance: cinza, sem rota — mesma regra dos
                      // itens do menu principal (migration 0041).
                      if (sub.permitido === false) {
                        return (
                          <li key={sub.id}>
                            <span
                              aria-disabled="true"
                              title={SEM_PERMISSAO}
                              className="flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs font-medium whitespace-nowrap text-foreground-300 cursor-not-allowed select-none"
                            >
                              <div className="w-3.5 h-3.5 flex items-center justify-center">
                                <i className={`${sub.icon} text-xs`}></i>
                              </div>
                              <span className="flex-1">{sub.label}</span>
                              <i className="ri-lock-line text-[10px]"></i>
                            </span>
                          </li>
                        );
                      }

                      return (
                        <li key={sub.id}>
                          <Link
                            to={`/condominios/${currentCondoId}/${sub.id}`}
                            className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-all ${
                              subActive
                                ? 'bg-primary-50 text-primary-700'
                                : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-800'
                            }`}
                          >
                            <div
                              className={`w-3.5 h-3.5 flex items-center justify-center ${subActive ? 'text-primary-600' : 'text-foreground-400'}`}
                            >
                              <i className={`${sub.icon} text-xs`}></i>
                            </div>
                            {sub.label}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              );
            }

            // Item que existe e a pessoa nao alcanca: cinza, sem rota. Some
            // do menu era ambiguo — nao dizia se a tela nao existe, se a conta
            // e limitada ou se algo quebrou (migration 0041).
            if (item.permitido === false) {
              return (
                <li key={item.href}>
                  <span
                    aria-disabled="true"
                    title={collapsed ? `${item.label} — ${SEM_PERMISSAO}` : SEM_PERMISSAO}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-foreground-300 cursor-not-allowed select-none ${
                      collapsed ? 'justify-center' : ''
                    }`}
                  >
                    <div className="w-5 h-5 flex items-center justify-center text-foreground-300">
                      <i className={`${item.icon} text-lg`}></i>
                    </div>
                    {!collapsed && (
                      <>
                        <span className="whitespace-nowrap flex-1">{item.label}</span>
                        <i className="ri-lock-line text-xs text-foreground-300"></i>
                      </>
                    )}
                  </span>
                </li>
              );
            }

            return (
              <li key={item.href}>
                <Link
                  to={item.href}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all ${
                    active
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-900'
                  } ${collapsed ? 'justify-center' : ''}`}
                  title={collapsed ? item.label : undefined}
                >
                  <div
                    className={`w-5 h-5 flex items-center justify-center ${active ? 'text-primary-600' : 'text-foreground-500'}`}
                  >
                    <i className={`${item.icon} text-lg`}></i>
                  </div>
                  {!collapsed && <span className="whitespace-nowrap">{item.label}</span>}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className={`border-t border-background-200/70 py-3 ${collapsed ? 'px-2' : 'px-4'}`}>
        <div className={`flex items-center gap-2.5 ${collapsed ? 'justify-center' : ''}`}>
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
              impersonatedAccount
                ? 'bg-accent-100'
                : isSuperuser
                  ? 'bg-accent-100'
                  : 'bg-primary-100'
            }`}
          >
            <span
              className={`text-xs font-semibold ${impersonatedAccount ? 'text-accent-700' : isSuperuser ? 'text-accent-700' : 'text-primary-700'}`}
            >
              {impersonatedAccount
                ? impersonatedAccount.nome.charAt(0).toUpperCase()
                : userIniciais}
            </span>
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground-900 truncate">{userName}</p>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 flex items-center justify-center text-foreground-400">
                  <i className={`${userRoleIcon} text-[10px]`}></i>
                </div>
                <p className="text-xs text-foreground-500 truncate">{userRole}</p>
              </div>
            </div>
          )}
        </div>
        {!collapsed && (
          <div className="flex flex-col gap-1 ml-2 pl-3 border-l-2 border-background-200/60">
            {impersonatedAccount && (
              <button
                onClick={handleExitImpersonation}
                className="flex items-center gap-2 px-2.5 py-1.5 text-sm text-accent-600 hover:text-accent-700 hover:bg-accent-50 rounded-md transition-colors w-full cursor-pointer font-medium"
              >
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-arrow-go-back-line text-xs"></i>
                </div>
                <span className="text-xs">Sair do modo cliente</span>
              </button>
            )}
            {rodapeMenu.map((item) =>
              item.permitido === false ? (
                <span
                  key={item.id}
                  aria-disabled="true"
                  title={SEM_PERMISSAO}
                  className="flex items-center gap-2 px-2.5 py-1.5 text-sm text-foreground-300 cursor-not-allowed select-none rounded-md w-full"
                >
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className={`${item.icon} text-xs`}></i>
                  </div>
                  <span className="text-xs flex-1">{item.label}</span>
                  <i className="ri-lock-line text-[10px]"></i>
                </span>
              ) : (
                <Link
                  key={item.id}
                  to={item.href}
                  className="flex items-center gap-2 px-2.5 py-1.5 text-sm text-foreground-500 hover:text-foreground-700 hover:bg-background-100 rounded-md transition-colors w-full"
                >
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className={`${item.icon} text-xs`}></i>
                  </div>
                  <span className="text-xs">{item.label}</span>
                </Link>
              ),
            )}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-2.5 py-1.5 text-sm text-foreground-500 hover:text-foreground-700 hover:bg-background-100 rounded-md transition-colors w-full cursor-pointer"
            >
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-logout-box-r-line text-xs"></i>
              </div>
              <span className="text-xs">Sair</span>
            </button>
          </div>
        )}
      </div>

      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-background-50 border border-background-200/70 flex items-center justify-center text-foreground-500 hover:text-foreground-700 shadow-sm transition-colors"
        aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
      >
        <i className={`ri-${collapsed ? 'arrow-right' : 'arrow-left'}-s-line text-xs`}></i>
      </button>
    </aside>
  );
}
